import * as Sentry from '@sentry/react';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { reportQueryError } from './query.sentry';

/**
 * The global React Query client.
 */
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
    queryCache: new QueryCache({
        onError: (error, query) => {
            // sentry capture all query errors
            Sentry.withScope((scope) => {
                const fingerprintPrefix = ['query', String(query.queryKey[0])];
                scope.setExtra('queryKey', query.queryKey);
                reportQueryError(error, fingerprintPrefix);
            });
        },
    }),
    mutationCache: new MutationCache({
        onError: (error, vars, _result, _mutation, context) => {
            // sentry capture all mutation errors
            Sentry.withScope((scope) => {
                const fingerprintPrefix = [
                    'mutation',
                    String(context.meta?.name ?? context.mutationKey?.[0]),
                ];
                scope.setTag(
                    'mutationKeyFirst',
                    JSON.stringify(context.mutationKey?.[0]),
                );
                scope.setExtra('mutationKey', context.mutationKey);
                scope.setExtra('mutationVars', vars);
                reportQueryError(error, fingerprintPrefix);
            });
        },
    }),
});

/**
 * Unwraps a query response and returns the first result(s).
 * Returns an empty array if the query result is undefined.
 *
 * @param response The query response.
 */
export async function resultsOf<T>(response: Promise<{ Result: T[] }>): Promise<T[]> {
    return (await response).Result;
}

/**
 * Unwraps a query response and returns the first result.
 * Returns undefined if the response contains an empty array.
 * Returns undefined if the query result is undefined.
 *
 * @param response The query response.
 */
export async function firstResultOf<T>(
    response: Promise<{ Result: T[] }>,
): Promise<T | undefined> {
    const res = await resultsOf(response);
    return res[0];
}

/**
 * Stabilizes a simple query key comprising one object which may contain arrays,
 * ensuring array order does not matter
 */
export function createStableQueryKey<
    TKey extends string,
    TParams extends Record<string, unknown>,
>(key: TKey, params: TParams): StableQueryKey<TKey, TParams> {
    return [
        key,
        Object.fromEntries(
            Object.entries(params).map(([k, v]) => [
                k,
                Array.isArray(v) ? v.toSorted() : v,
            ]),
        ) as TParams,
    ] as const;
}

export type StableQueryKey<TKey, TParams> = readonly [TKey, TParams];
